import { Badge, Box, Group, Stack, Text, ActionIcon, ThemeIcon, Tooltip } from '@mantine/core';
import { Pencil, RefreshCw, Trash2 } from 'lucide-react';
import type { ProviderConnection } from '@bmai/connections-api-client';
import type { ProviderType } from '@bmai/shared';
import { SurfaceCard } from '@/design/SurfaceCard';
import { providerIconForType } from '@/shared/ProviderIconComponent';
import { formatLastActive, formatTimestamp } from '@/shared/DateFormatting';
import type { ConnectionIngestionStatus } from './IntegrationsService';

const AUTH_METHOD_LABELS: Record<string, string> = {
    ApiKey: 'Api key',
    OAuth: 'OAuth',
    ServiceAccount: 'Service Account',
    Webhook: 'Webhook',
    AssumedRole: 'Assumed Role',
    ClientCredentials: 'Client Credentials',
};

function authMethodLabel(method: string): string {
    return AUTH_METHOD_LABELS[method] ?? method.replace(/([a-z])([A-Z])/g, '$1 $2');
}

/** A run older than this is treated as stale. */
const STALE_AFTER_MS = 7 * 24 * 60 * 60 * 1000;

interface ConnectionStatusBadge {
    label: string;
    color: string;
    tooltip: string;
}

/**
 * Derives the connection-health badge from the ingestion/sync facts:
 *   - disabled           → Paused  (no runs while disabled)
 *   - no run yet         → Enabled (on, waiting for the first run)
 *   - last run failed    → Stale   (ran recently but errored)
 *   - succeeded ≤ 7d ago → Connected
 *   - older              → Stale   (a run failed or the schedule stopped)
 */
function deriveStatusBadge(enabled: boolean, status: ConnectionIngestionStatus | undefined): ConnectionStatusBadge {
    if (!enabled) {
        return { label: 'Paused', color: 'gray', tooltip: 'Disabled — no data is being collected for this connection.' };
    }
    const lastChecked = status?.lastCheckedAt;
    if (!lastChecked) {
        return { label: 'Enabled', color: 'blue', tooltip: 'Enabled — waiting for the first run.' };
    }
    if (status?.completeness === 'failed') {
        return { label: 'Stale', color: 'yellow', tooltip: `Last run failed ${formatTimestamp(lastChecked)}.` };
    }
    const lastMs = new Date(lastChecked).getTime();
    const fresh = !isNaN(lastMs) && Date.now() - lastMs <= STALE_AFTER_MS;
    if (fresh) {
        return {
            label: 'Connected',
            color: 'green',
            tooltip: `Last synced ${formatTimestamp(lastChecked)} · ${(status?.recordsIngested ?? 0).toLocaleString()} records`,
        };
    }
    return { label: 'Stale', color: 'yellow', tooltip: `No successful run since ${formatTimestamp(lastChecked)}.` };
}

interface IntegrationConnectionCardProps {
    connection: ProviderConnection;
    providerType: ProviderType | undefined;
    status: ConnectionIngestionStatus | undefined;
    onEdit: (conn: ProviderConnection) => void;
    onDelete: (conn: ProviderConnection) => void;
    onReauthenticate?: (conn: ProviderConnection) => void;
}

export default function IntegrationConnectionCard({ connection, providerType, status, onEdit, onDelete, onReauthenticate }: IntegrationConnectionCardProps): React.ReactElement {
    const providerName = providerType?.displayName ?? connection.providerTypeId;
    const cardTitle = connection.displayName ?? providerName;
    const authMethod = (connection.config as { authMethod?: string }).authMethod;
    const icon = providerIconForType(connection.providerTypeId);
    const isOAuth = authMethod === 'OAuth';
    const statusBadge = deriveStatusBadge(connection.enabled, status);

    return (
        <SurfaceCard $padding="md">
            <Stack gap={10}>
                <Group justify="space-between" align="flex-start" wrap="nowrap">
                    <Group gap="sm" wrap="nowrap" align="center" style={{ minWidth: 0 }}>
                        {icon && (
                            <ThemeIcon size={32} radius="md" variant="transparent">
                                {icon}
                            </ThemeIcon>
                        )}
                        <Box style={{ minWidth: 0 }}>
                            <Text fw={700} size="sm" truncate>{cardTitle}</Text>
                        </Box>
                    </Group>
                    <Group gap={4} wrap="nowrap">
                        {isOAuth && onReauthenticate && (
                            <Tooltip label="Re-authenticate (rotate refresh token; keeps other config)">
                                <ActionIcon variant="subtle" size="sm" onClick={() => onReauthenticate(connection)}>
                                    <RefreshCw size={14} />
                                </ActionIcon>
                            </Tooltip>
                        )}
                        <Tooltip label="Edit">
                            <ActionIcon variant="subtle" size="sm" onClick={() => onEdit(connection)}>
                                <Pencil size={14} />
                            </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Delete">
                            <ActionIcon variant="subtle" color="red" size="sm" onClick={() => onDelete(connection)}>
                                <Trash2 size={14} />
                            </ActionIcon>
                        </Tooltip>
                    </Group>
                </Group>
                <Group justify="space-between" align="center" gap="xs" wrap="nowrap">
                    <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
                        {status?.lastCheckedAt
                            ? `Checked ${formatLastActive(status.lastCheckedAt)}`
                            : `Updated ${new Date(connection.updatedAt).toLocaleDateString()}`}
                    </Text>
                    <Group gap="xs" wrap="wrap" justify="flex-end" style={{ minWidth: 0 }}>
                        <Tooltip label={statusBadge.tooltip}>
                            <Badge size="sm" color={statusBadge.color} variant="light">{statusBadge.label}</Badge>
                        </Tooltip>
                        {authMethod && (
                            <Badge size="sm" variant="light" color="indigo">{authMethodLabel(authMethod)}</Badge>
                        )}
                    </Group>
                </Group>
            </Stack>
        </SurfaceCard>
    );
}
